import { Workspace } from '@/features/iam/workspaces/Workspace'
import { Model, type ModelProps } from '@/infra/model'
import { Id } from '@/infra/id'
import { type Conversation } from '@/features/messaging/Conversation'
import { computed, observable } from 'mobx'
import { unixEpoch } from '@/utils/date-util'
import { type Resolution, type WaitingOn } from '@/features/jobs/Job'
import { type JobStatus } from '@/features/jobs/job-status'
import { type JobProgressionStepStatus } from '@/features/jobs/taxes/progression/JobProgression'
import { type JobScopeChangeStatus } from '@/features/jobs/job-scope-changes/JobScopeChange'

export type SearchableJobProps = ModelProps<SearchableJob>

export class SearchableJob extends Model<SearchableJob> {
  static readonly none = new SearchableJob()

  id = Id.none

  @observable accessor resolution: Resolution | null | undefined = null
  @observable accessor dateDeadline: Date | null = null
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateSubmitted: Date | null = null
  @observable accessor dateAccepted: Date | null = null
  @observable accessor dateClosed: Date | null = null
  @observable accessor dateModified: Date | null = null
  @observable accessor dateLastActivity: Date | null = null
  @observable accessor dateLastProMessageActivity: Date | null = null
  @observable accessor dateLastClientMessageActivity: Date | null = null
  @observable
  accessor dateLastRelationshipManagerMessageActivity: Date | null = null
  @observable accessor dateAdvanceAllowed: Date | null = null

  @observable accessor workspace = Workspace.none
  @observable accessor legendName = ''
  @observable accessor legendId = Id.none
  @observable accessor legendVersion = 0
  @observable accessor name = ''
  /**
   * The computed job title.
   */
  @observable accessor description = ''
  @observable accessor shortId = ''
  @observable accessor status: JobStatus = 'UnderConstruction'
  @observable accessor progressionStep: JobProgressionStepStatus = 'Unspecified'

  @observable accessor members: SearchableMember[] = []
  @observable accessor waitingOn: WaitingOn | null = null
  @observable accessor jobScopeItems: string[] = []
  @observable accessor clientTeamName: string | undefined = undefined
  @observable accessor clientTeamId = Id.none
  @observable accessor proTeamName: string | undefined = undefined
  @observable accessor proTeamId = Id.none
  @observable accessor conversation: Conversation | null = null
  @observable accessor providerCutAmount: number | undefined = undefined
  @observable accessor total: number | undefined = undefined
  @observable accessor archived = false
  @observable accessor isRequestedPro: boolean | undefined = false
  @observable accessor scopeChangeStatus: JobScopeChangeStatus = 'Unspecified'

  /**
   * Display name.
   */
  @computed
  get displayName() {
    return this.name || this.description || this.legendName
  }

  /**
   * Client members on the job.
   */
  @computed
  get clientMembers() {
    return this.members.filter((c) => c.type === 'Client').map((c) => c)
  }

  /**
   * Pro members on the job.
   */
  @computed
  get proMembers() {
    return this.members.filter((c) => c.type === 'Pro').map((c) => c)
  }

  /**
   * The champion client.
   */
  @computed
  get championClient() {
    return this.members.find(
      (c) => c.type === 'Client' && c.role === 'Champion',
    )
  }

  /**
   * The champion provider.
   */
  @computed
  get championPro() {
    return this.members.find((c) => c.type === 'Pro' && c.role === 'Champion')
  }

  /**
   * The current user on the job.
   */
  @computed
  get currentUserMember() {
    return this.members.find((m) => m.isCurrentUser) ?? null
  }

  /**
   * The champion of the opposite type than the current user.
   */
  @computed
  get otherChampion() {
    return this.members.find(
      (c) => c.role === 'Champion' && c.type !== this.currentUserMember?.type,
    )
  }

  /**
   * Whether the job is a draft.
   */
  @computed
  get isDraft() {
    return this.status === 'UnderConstruction'
  }
}

/**
 * Member.
 */
export interface SearchableMember {
  readonly id: Id
  readonly displayName: string
  readonly avatar: string
  readonly avatarFallback: string | null
  readonly type: SearchableMemberType
  readonly role: SearchableMemberRoleType
  readonly isCurrentUser: boolean
  readonly email?: string | null
}

/**
 * Member type.
 */
export type SearchableMemberType = 'Unspecified' | 'Pro' | 'Client'

/**
 * Member role type.
 */
export type SearchableMemberRoleType = 'Unspecified' | 'Supporting' | 'Champion'
